import { isAdrCommodity } from '@/modules/road-transport/constants';
import type { ShipmentCargoLine } from '../types';

/** Longest cargo line description the TMS accepts; the WebAPI rejects anything longer. */
export const CARGO_DESCRIPTION_MAX_LENGTH = 30;

/**
 * Beside a package entry the pallet count goes to BluJay's "# Units" field, which takes at most
 * 999; the WebAPI rejects anything higher. A pallet-only line has no cap.
 */
export const MAX_PALLET_QUANTITY_WITH_PACKAGE = 999;

export function createEmptyCargoLine(): ShipmentCargoLine {
  return {
    id: `cargo-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
    quantity: 0,
    unit: '',
    palletQuantity: 0,
    palletType: '',
    packageQuantity: 0,
    packageType: '',
    commodity: '',
    grossWeightKg: 0,
    netWeightKg: 0,
    lengthM: 0,
    widthM: 0,
    heightM: 0,
    ldmVol: 0,
    description: '',
  };
}

/** How a cargo line states what is being transported: Quantity + Unit, or pallet/package entries. */
export interface CargoQuantityValidator {
  /** True when none of the quantity fields have been touched. */
  isBlank(line: ShipmentCargoLine): boolean;
  isComplete(line: ShipmentCargoLine): boolean;
  /** True when the line has no complete pallet or package entry at all. */
  isMissingPalletOrPackage(line: ShipmentCargoLine): boolean;
  /** True when the pallet quantity exceeds what BluJay accepts beside a package entry. */
  isPalletQuantityOverLimit(line: ShipmentCargoLine): boolean;
  isPalletQuantityMissing(line: ShipmentCargoLine): boolean;
  isPalletTypeMissing(line: ShipmentCargoLine): boolean;
  isPackageQuantityMissing(line: ShipmentCargoLine): boolean;
  isPackageTypeMissing(line: ShipmentCargoLine): boolean;
}

class UnitCargoQuantityValidator implements CargoQuantityValidator {
  isBlank(line: ShipmentCargoLine): boolean {
    return line.quantity <= 0 && line.unit.trim() === '';
  }

  isComplete(line: ShipmentCargoLine): boolean {
    return line.quantity > 0 && line.unit.trim() !== '';
  }

  isMissingPalletOrPackage(): boolean {
    return false;
  }

  isPalletQuantityOverLimit(): boolean {
    return false;
  }

  isPalletQuantityMissing(): boolean {
    return false;
  }

  isPalletTypeMissing(): boolean {
    return false;
  }

  isPackageQuantityMissing(): boolean {
    return false;
  }

  isPackageTypeMissing(): boolean {
    return false;
  }
}

/**
 * At least one complete entry (type + quantity), pallet and/or package. An entry with only one
 * half filled in is incomplete, even beside a complete one.
 */
class PalletPackageCargoQuantityValidator implements CargoQuantityValidator {
  isBlank(line: ShipmentCargoLine): boolean {
    return !this.isPalletStarted(line) && !this.isPackageStarted(line);
  }

  isComplete(line: ShipmentCargoLine): boolean {
    return (
      !this.isMissingPalletOrPackage(line) &&
      !this.isPalletQuantityOverLimit(line) &&
      (!this.isPalletStarted(line) || this.isPalletComplete(line)) &&
      (!this.isPackageStarted(line) || this.isPackageComplete(line))
    );
  }

  isMissingPalletOrPackage(line: ShipmentCargoLine): boolean {
    return !this.isPalletComplete(line) && !this.isPackageComplete(line);
  }

  isPalletQuantityOverLimit(line: ShipmentCargoLine): boolean {
    return line.palletQuantity > MAX_PALLET_QUANTITY_WITH_PACKAGE && this.isPackageComplete(line);
  }

  isPalletQuantityMissing(line: ShipmentCargoLine): boolean {
    return line.palletQuantity <= 0 && (line.palletType !== '' || this.isMissingPalletOrPackage(line));
  }

  isPalletTypeMissing(line: ShipmentCargoLine): boolean {
    return line.palletType === '' && (line.palletQuantity > 0 || this.isMissingPalletOrPackage(line));
  }

  isPackageQuantityMissing(line: ShipmentCargoLine): boolean {
    return line.packageQuantity <= 0 && (line.packageType !== '' || this.isMissingPalletOrPackage(line));
  }

  isPackageTypeMissing(line: ShipmentCargoLine): boolean {
    return line.packageType === '' && (line.packageQuantity > 0 || this.isMissingPalletOrPackage(line));
  }

  private isPalletStarted(line: ShipmentCargoLine): boolean {
    return line.palletQuantity > 0 || line.palletType !== '';
  }

  private isPalletComplete(line: ShipmentCargoLine): boolean {
    return line.palletQuantity > 0 && line.palletType !== '';
  }

  private isPackageStarted(line: ShipmentCargoLine): boolean {
    return line.packageQuantity > 0 || line.packageType !== '';
  }

  private isPackageComplete(line: ShipmentCargoLine): boolean {
    return line.packageQuantity > 0 && line.packageType !== '';
  }
}

export const unitCargoQuantityValidator = new UnitCargoQuantityValidator();
export const palletPackageCargoQuantityValidator = new PalletPackageCargoQuantityValidator();

export function resolveCargoQuantityValidator(isPalletPackageEnabled: boolean): CargoQuantityValidator {
  return isPalletPackageEnabled ? palletPackageCargoQuantityValidator : unitCargoQuantityValidator;
}

function hasCargoLineDimensions(line: ShipmentCargoLine): boolean {
  return line.lengthM > 0 && line.widthM > 0 && line.heightM > 0;
}

export function hasCargoLineLdmOrDimensions(line: ShipmentCargoLine): boolean {
  return line.ldmVol > 0 || hasCargoLineDimensions(line);
}

/** True when the row has no user-entered cargo data (blank trailing row). */
export function isCargoLineBlank(
  line: ShipmentCargoLine,
  quantityValidator: CargoQuantityValidator,
): boolean {
  return (
    quantityValidator.isBlank(line) &&
    line.commodity.trim() === '' &&
    line.grossWeightKg <= 0 &&
    line.netWeightKg <= 0 &&
    line.lengthM <= 0 &&
    line.widthM <= 0 &&
    line.heightM <= 0 &&
    line.ldmVol <= 0 &&
    line.description.trim() === ''
  );
}

export function isPersistedCargoLineId(lineId: string): boolean {
  return !lineId.startsWith('cargo-') && Number.isFinite(Number(lineId));
}

export function isCargoLineComplete(
  line: ShipmentCargoLine,
  quantityValidator: CargoQuantityValidator,
): boolean {
  return (
    quantityValidator.isComplete(line) &&
    line.commodity.trim() !== '' &&
    line.grossWeightKg > 0 &&
    hasCargoLineLdmOrDimensions(line)
  );
}

/**
 * Why a draft save cannot go ahead, or null when it can.
 *
 * Empty cargo (only blank rows) is allowed on a draft. Only complete lines are written to
 * the order, so a started-but-incomplete line would be dropped silently by the save —
 * blocking here keeps "Draft saved" honest.
 */
export type CargoDraftSaveIssue =
  | 'missingPalletOrPackage'
  | 'palletQuantityOverLimit'
  | 'missingLdmOrDimensions'
  | 'incompleteLine';

export function getCargoDraftSaveIssue(
  lines: ShipmentCargoLine[],
  quantityValidator: CargoQuantityValidator,
): CargoDraftSaveIssue | null {
  const started = lines.filter((line) => !isCargoLineBlank(line, quantityValidator));
  if (started.length === 0) return null;

  // Reported ahead of the generic message: these are the requirements the column headers
  // do not mark, since either of two field groups satisfies each.
  if (started.some((line) => quantityValidator.isMissingPalletOrPackage(line))) {
    return 'missingPalletOrPackage';
  }
  if (started.some((line) => quantityValidator.isPalletQuantityOverLimit(line))) {
    return 'palletQuantityOverLimit';
  }
  if (!started.every(hasCargoLineLdmOrDimensions)) return 'missingLdmOrDimensions';

  return started.every((line) => isCargoLineComplete(line, quantityValidator))
    ? null
    : 'incompleteLine';
}

export function areCargoLinesValidForDraftSave(
  lines: ShipmentCargoLine[],
  quantityValidator: CargoQuantityValidator,
): boolean {
  return getCargoDraftSaveIssue(lines, quantityValidator) === null;
}

/**
 * One started line at minimum, and nothing half-filled: only complete lines reach the order, so
 * letting a started-but-incomplete one through here would drop it silently on submit.
 */
export function isCargoLinesStepValid(
  lines: ShipmentCargoLine[],
  quantityValidator: CargoQuantityValidator,
): boolean {
  const started = lines.filter((line) => !isCargoLineBlank(line, quantityValidator));
  return (
    started.length > 0 && started.every((line) => isCargoLineComplete(line, quantityValidator))
  );
}

export function cargoRequiresMsds(lines: ShipmentCargoLine[]): boolean {
  return lines.some((line) => isAdrCommodity(line.commodity));
}
